import axios from 'axios';
import { LiquorLicenceValidationResponse } from '../Types/liquorLicenceValidationResponse';
import { SERVER_URL } from '../Constants';
import moment from 'moment';

export const DateFormat = {
	// 27th Feb 2023
	UxFriendlyDate: 'Do MMM YYYY'
}

export function lowerCaseFirst(str: string) {
	return str[0].toLowerCase() + str.slice(1);
}

export function upperCaseFirst(str: string) {
	return str[0].toUpperCase() + str.slice(1);
}

export function camelCaseIntoWords(name: string) {
	return name.replace(/([A-Z])/g, ' $1')
		.replace(/^./, str => str.toUpperCase());
}

export function camelCase(str: string) {
	const words = str.split(' ');
	const allLowerCase = words.map(w => w.toLowerCase());
	return allLowerCase[0] + allLowerCase.slice(1).map(upperCaseFirst).join('');
}

export function lowerCaseNoSpaces(str: string) {
	return str.toLowerCase().replace(/ /g, '');
}

export function noSpaces(str: string) {
	return str.replace(/ /g, '');
}

/**
 * Formats a number of bytes as a string
 * Implementation taken from https://stackoverflow.com/a/18650828/8426638
 * @param bytes The byte amount to format
 * @param decimals The number of decimals allowed
 */
export function formatBytes(bytes: number, decimals = 2) {
	if (bytes === 0) return '0 Bytes';

	const k = 1024;
	const dm = decimals < 0 ? 0 : decimals;
	const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

	const i = Math.floor(Math.log(bytes) / Math.log(k));

	// eslint-disable-next-line prefer-template
	return parseFloat((bytes / (k ** i)).toFixed(dm)) + ' ' + sizes[i];
}

// % protected region % [Add extra StringUtil functions here] on begin
export function formatAbn(abn?: string) {
	if (abn && abn.length >= 11) {
		return `${abn.slice(0, 2)} ${abn.slice(2, 5)} ${abn.slice(5, 8)} ${abn.slice(8)}`;
	}
	return abn;
}

export function LocaleCompareAsc(a: string, b: string): number {
	return a.localeCompare(b);
}
export function LocaleCompareAttrAsc(attr: string): (a: any, b: any) => number {
	return (a, b) => LocaleCompareAsc(a[attr], b[attr]);
}
export function LocaleCompareObjectAsc(obj: { [key: string]: string }): (a: string, b: string) => number {
	return (a, b) => LocaleCompareAsc(obj[a], obj[b]);
}

const numberFormatWithSymbol = Intl.NumberFormat('en-US', {
	style: 'currency',
	currency: 'AUD',
	currencyDisplay: 'narrowSymbol',
});
const numberFormatWithoutSymbol = Intl.NumberFormat('en-US', {
	style: 'decimal',
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
});
const totalNumberDecimalFormat = Intl.NumberFormat('en-US', {
	style: 'decimal',
});
export function LocaleFormatCurrency(amount: number, includeSymbol: boolean = true): string {
	return includeSymbol
		? numberFormatWithSymbol.format(amount)
		: numberFormatWithoutSymbol.format(amount);
}
export function LocaleFormatNumberDecimal(amount: number): string {
	return totalNumberDecimalFormat.format(amount);
}

export function safeGqlSearch(search: string) {
	return search.replace(/["\\]/g, '');
}


// eslint-disable-next-line max-len
export function validateLiquorLicenceNumber(licenceNumber: string, licenceState: string, customerId?: string) {
	return axios.post(`${SERVER_URL}/api/liquor-licence-validation`, {
		licenceNumber,
		state: licenceState,
		customerId: customerId,
	}).then(data => {
		return { data: data.data } as LiquorLicenceValidationResponse;
	}).catch(exception => {
		return { exception: { exceptionDescription: exception.response.data } } as LiquorLicenceValidationResponse;
	});
}

export function pluralise(count: number, word: string, pluralPart: string = 's', includeCount: boolean = true): string {
	if (count === 1) {
		return `${includeCount ? count : ''} ${word}`.trim();
	}

	const removeTrailingCharCount = pluralPart.lastIndexOf('.') + 1;

	let wordPlural = removeTrailingCharCount > 0
		? word.substring(0, word.length - removeTrailingCharCount)
		: word;

	wordPlural += pluralPart.substring(removeTrailingCharCount);

	return `${includeCount ? count : ''} ${wordPlural}`.trim();
}

export function isNumeric(n: string) {
	return !Number.isNaN(n) && !Number.isNaN(parseFloat(n));
}

export const EmptyGuid: string = '00000000-0000-0000-0000-000000000000';

export function booleanToActive(bool?: boolean): string {
	return bool === true ? 'Active' : 'Inactive';
}

export function toTitleCase(value: string): string {
	return value
		.toLowerCase()
		.split(' ')
		.map(word => 
			word
				.split("'")
				.map(part => part.charAt(0).toUpperCase() + part.slice(1))
				.join("'")
		)
		.join(' ');
}

export function validateVin(vin: string): boolean {
	const parsedVin = vin.toUpperCase();

	return parsedVin.length === 17 &&
		/^[A-Z0-9]+$/.test(parsedVin) &&
		!/[IOQ]/.test(parsedVin) &&
		/^[0-9]+$/.test(parsedVin.slice(14));
}

export function validateRego(value: string): boolean {
	const parsedValue = value.toUpperCase();

	return (
		parsedValue.length <= 9 &&
		/^[A-Z0-9]+$/.test(parsedValue)
	);
}

export function isGuid(id: string): boolean {
	const guidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
	return guidRegex.test(id);
}

export function formatString(value: string | null, defaultDisplayValue: string = '-') {
	if (value == null) return defaultDisplayValue;
	const parsedValue = value.trim();
	if (parsedValue == "") return defaultDisplayValue;
	return parsedValue;
}

export function formatDateString(value:string | undefined | null, format: string = 'Do MMMM YYYY', defaultDisplayValue: string = '-') {
	if (value == null) return  defaultDisplayValue;
	const parsedValue = value.trim();
	if (parsedValue === "") return defaultDisplayValue;
	return moment(parsedValue).format(format)
}
// % protected region % [Add extra StringUtil functions here] end

